import type { BulleEnrichie } from '../partage/api/types';
import type { NoeudAPlacer } from './placeDeLaHierarchie';

/**
 * Ce que la vue « Hiérarchie » montre (ticket 35) : l'arbre des bulles visibles, à partir de
 * ce que chaque feuille dépliée contient et de l'ordre que le serveur lui a donné. Une étape
 * repliée cache tout ce qu'elle contient ; une étape dépliée pas encore lue se montre repliée
 * le temps de la lecture.
 */

/**
 * Ce qu'une feuille contient, et dans quel ordre de haut en bas. `B` : la forme de ses bulles (la
 * Liste lit celles de la feuille, avec leur « N sur M »).
 */
export interface Contenu<B extends BulleEnrichie = BulleEnrichie> {
  enfants: B[];
  ordre: string[];
}

export interface BulleVisible<B extends BulleEnrichie = BulleEnrichie> {
  bulle: B;
  /** 1 pour une bulle de la feuille ouverte, 2 pour ce qu'elle contient, et ainsi de suite. */
  niveau: number;
}

export function arbreVisible<B extends BulleEnrichie = BulleEnrichie>(
  centreId: string,
  contenuDe: (id: string) => Contenu<B> | undefined,
  ouvertes: ReadonlySet<string>
): { arbre: NoeudAPlacer; visibles: Map<string, BulleVisible<B>> } {
  const visibles = new Map<string, BulleVisible<B>>();

  function construire(id: string, niveau: number): NoeudAPlacer {
    const deplie = ouvertes.has(id);
    const contenu = deplie ? contenuDe(id) : undefined;
    const dedans: NoeudAPlacer[] = [];
    if (contenu) {
      const parId = new Map(contenu.enfants.map((e) => [e.id, e]));
      for (const idEnfant of contenu.ordre) {
        const enfant = parId.get(idEnfant);
        if (!enfant) continue;
        visibles.set(enfant.id, { bulle: enfant, niveau: niveau + 1 });
        dedans.push(construire(enfant.id, niveau + 1));
      }
    }
    return { id, enfants: dedans, deplie };
  }

  return { arbre: construire(centreId, 0), visibles };
}

/** Les étapes dépliées ET visibles, sauf le centre : celles dont il faut lire la feuille. */
export function deplieesVisibles(
  centreId: string,
  visibles: ReadonlyMap<string, BulleVisible<BulleEnrichie>>,
  ouvertes: ReadonlySet<string>
): string[] {
  return [...ouvertes].filter((id) => id !== centreId && visibles.has(id)).sort();
}
